import { useEffect, useState } from 'react';
import { Icon, type IconName } from './icon';

/**
 * Whether the page has scrolled from its top: the app bar takes a surface colour then, and a FAB
 * folds to its icon. One passive listener, read once per frame.
 */
export function useScrolled(): boolean {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    let frame = 0;
    const check = () => {
      frame = 0;
      setScrolled(window.scrollY > 0);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(check);
    };
    check();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(frame);
    };
  }, []);
  return scrolled;
}

interface MainActionProps {
  icon: IconName;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  /** Why it is disabled, as a tooltip. */
  title?: string;
  testId?: string;
}

/**
 * A screen's main creation action (TF-90): the filled button in the page head on a computer, and on
 * a phone or tablet Material's FAB at the bottom right instead, extended with its label while the
 * page is at its top. Only one of the two is ever shown (global.css, by window size), so the action
 * has one name for screen readers and tests.
 */
export function MainAction({ icon, label, onClick, disabled, title, testId }: MainActionProps) {
  const scrolled = useScrolled();
  return (
    <>
      <button type="button" className="btn btn-filled main-action" onClick={onClick} disabled={disabled} data-tooltip={title} data-testid={testId}>
        <Icon name={icon} size={18} />
        {label}
      </button>
      <button type="button" className={scrolled ? 'fab' : 'fab fab-extended'} onClick={onClick} disabled={disabled} data-tooltip={title ?? label} aria-label={label}>
        <Icon name={icon} />
        <span className="fab-label" aria-hidden="true">
          {label}
        </span>
      </button>
    </>
  );
}
